import React from 'react';
import { Button, Dropdown, Space, Tooltip } from 'antd';

import { RecordIconTile, RecordQualifier, RecordTileStack } from './RecordBadge';
import { CHEVRON_DOWN, COLLECTION_ICON, PERSON, PIN, PIN_ANGLE } from '../icons';
import useCollectionIdentity from '../../hooks/useCollectionIdentity';
import { qualifierSpokenText } from '../../utils/recordQualifier';

/**
 * A row of one-click launchers: favorite cards, quick add templates, anything
 * that "opens the new-item form with X". One rule for every caller: at most
 * `max` buttons in the row, and the rest behind a single "N more" dropdown
 * whose items launch on click — still one click, just behind a door.
 *
 * Entries are generic: `{ key, record, records, kind, label, scope, title, onLaunch }`.
 *   record  — the record whose tile is the button's identity (a card, usually)
 *   records — several records whose tiles STACK as the identity (a multi add
 *             form: the cards its steps create); wins over `record`
 *   kind    — the record kind for the tile's icon fallback ('card' | 'collection' | 'item')
 *   scope   — 'collection' | 'user' | null: which tier pinned it; drives the
 *             glyph and its tooltip (the same words the card picker rows use)
 *   title   — optional tooltip override for the whole button
 *   collection — optional: the OTHER collection the launcher MAKES ITS
 *             ITEM IN (a parent's shortcut for a sub-collection's card:
 *             Health). The button then reads like a qualified badge: the
 *             collection's stacked tile and name, a divider, then the
 *             launcher — and is named "Weekly vitals, in Health", launcher
 *             first. `collectionNote` is the segment's tooltip (a string, or
 *             a function of the resolved identity). Nothing is drawn — no
 *             segment, no divider — while the collection cannot be named.
 *   note    — optional muted words after the label. `noteTitle` is its
 *             tooltip. On a phone it sits under the label, so it is never
 *             squeezed out. (It carried "shared from {parent}" until KPR-1084
 *             ended inherited launchers; no row sets it today.)
 *   tip     — optional: show `title` as a tooltip that opens on hover AND
 *             keyboard focus (antd), instead of the native title that opens on
 *             hover only. Quick add sets it, its label being the one that
 *             ellipsizes on a phone and its title naming the full label.
 *   onLaunch— called with no arguments when the entry is launched
 *   pin     — optional `{ pinned, onToggle }`: the entry can go on the
 *             person's Home. The button then carries a square pin toggle
 *             joined to its right (Space.Compact — actions rightmost), and an
 *             overflowed entry's toggle sits in the "N more" menu's own
 *             "Home" group. Never the collection tier's "Pin for everyone".
 *
 * The caller owns the ORDER: collection tier first, then user tier, name order
 * within each. This component never sorts — a caller that already ordered its
 * entries would otherwise be second-guessed.
 *
 * Renders nothing when there are no entries and no `emptyText`.
 */

export const LAUNCH_SCOPE_TITLES = {
	collection: 'Pinned for everyone by a collection manager — managed in collection settings',
	user: 'Your favorite',
};

const SCOPE_GLYPHS = {
	collection: COLLECTION_ICON,
	user: PERSON,
};

function ScopeGlyph({ scope }) {
	const Glyph = SCOPE_GLYPHS[scope];
	if (!Glyph) return null;
	return (
		<span
			className={`launch-row__scope launch-row__scope--${scope}`}
			title={LAUNCH_SCOPE_TITLES[scope]}
			aria-hidden="true"
		>
			<Glyph />
		</span>
	);
}

function EntryFace({ entry }) {
	// Resolved once: the segment shows it, the words after the label say it.
	const where = useCollectionIdentity(entry.collection || null);
	return (
		<>
			{where ? (
				<RecordQualifier
					resolved={where}
					size="xs"
					note={entry.collectionNote || null}
					spoken={false}
					divider
					className="launch-row__qualifier"
				/>
			) : null}
			{Array.isArray(entry.records)
				? <RecordTileStack records={entry.records} kind={entry.kind || 'card'} size="xs" />
				: <RecordIconTile record={entry.record} kind={entry.kind || 'card'} size="xs" />}
			<span className="launch-row__text">
				<span className="launch-row__label">{entry.label}</span>
				{where ? <span className="record-badge__spoken">{`, ${qualifierSpokenText(where)}`}</span> : null}
				{entry.note ? <span className="launch-row__note" title={entry.noteTitle || undefined}>{entry.note}</span> : null}
			</span>
			<ScopeGlyph scope={entry.scope} />
		</>
	);
}

// A qualified launcher (it carries its own segment tooltip) keeps the native
// title: two tooltips would open at once over the segment.
const tipped = entry => Boolean(entry.tip && !entry.collection);

const entryTitle = entry => entry.title ?? LAUNCH_SCOPE_TITLES[entry.scope] ?? undefined;

export default function LaunchButtonRow({
	entries = [],
	max = 5,
	moreLabel = n => `${n} more`,
	emptyText,
	ariaLabel,
	// Where the overflow menu mounts. A host inside a modal keeps it beside
	// the trigger (as the card select does) so a touch drag scrolls the menu,
	// not the page behind the modal.
	getPopupContainer,
}) {
	const list = Array.isArray(entries) ? entries.filter(Boolean) : [];

	if (!list.length) {
		if (!emptyText) return null;
		return <div className="launch-row launch-row--empty">{emptyText}</div>;
	}

	const cap = Math.max(0, Number.isFinite(max) ? max : 5);
	const visible = list.slice(0, cap);
	const overflow = list.slice(cap);

	const pinnable = overflow.filter(entry => entry.pin && typeof entry.pin.onToggle === 'function');
	const menu = overflow.length ? {
		items: [
			...overflow.map(entry => ({
				key: entry.key,
				label: <span className="launch-row__menu-item"><EntryFace entry={entry} /></span>,
				title: entryTitle(entry),
				onClick: () => { if (typeof entry.onLaunch === 'function') entry.onLaunch(); },
			})),
			...(pinnable.length ? [
				{ type: 'divider', key: 'pin-divider' },
				{
					type: 'group',
					key: 'pin-group',
					label: 'Home',
					children: pinnable.map(entry => ({
						key: `pin:${entry.key}`,
						icon: entry.pin.pinned ? <PIN /> : <PIN_ANGLE />,
						label: `${entry.pin.pinned ? 'Unpin' : 'Pin'} ${entry.label} ${entry.pin.pinned ? 'from' : 'to'} Home`,
						onClick: () => entry.pin.onToggle(),
					})),
				},
			] : []),
		],
	} : null;

	const launchButton = entry => {
		const button = (
			<Button
				key={entry.key}
				className="launch-row__button"
				title={tipped(entry) ? undefined : entryTitle(entry)}
				onClick={() => { if (typeof entry.onLaunch === 'function') entry.onLaunch(); }}
			>
				<EntryFace entry={entry} />
			</Button>
		);
		return tipped(entry) ? <Tooltip key={entry.key} trigger={['hover', 'focus']} title={entryTitle(entry)}>{button}</Tooltip> : button;
	};

	return (
		<div className="launch-row" role="group" aria-label={ariaLabel}>
			{visible.map(entry => (entry.pin && typeof entry.pin.onToggle === 'function' ? (
				<Space.Compact key={entry.key} className="launch-row__pair">
					{launchButton(entry)}
					<Button
						className={`launch-row__pin${entry.pin.pinned ? ' launch-row__pin--on' : ''}`}
						icon={entry.pin.pinned ? <PIN /> : <PIN_ANGLE />}
						aria-pressed={Boolean(entry.pin.pinned)}
						aria-label={`${entry.pin.pinned ? 'Unpin' : 'Pin'} ${entry.label} ${entry.pin.pinned ? 'from' : 'to'} Home`}
						title={entry.pin.pinned ? 'On your Home — click to unpin' : 'Pin to your Home'}
						onClick={() => entry.pin.onToggle()}
					/>
				</Space.Compact>
			) : launchButton(entry)))}
			{menu ? (
				<Dropdown trigger={['click']} menu={menu} placement="bottomLeft" getPopupContainer={getPopupContainer}>
					<Button className="launch-row__more" aria-haspopup="menu">
						<span className="launch-row__label">{moreLabel(overflow.length)}</span>
						<CHEVRON_DOWN className="launch-row__chevron" aria-hidden="true" />
					</Button>
				</Dropdown>
			) : null}
		</div>
	);
}
